import * as React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font
} from "@react-pdf/renderer";
import { IFormSectionPresentationModel } from "../../../../presenter/form/model/FormSectionPresentationModel";
import DisplayFormPDF from "./DisplayFormPDF";
import { IReport1Applicant } from "../../../../model/report/Report1Applicant";
import { IFormVersionItem } from "../../../../model/report/FormVersionItem";
import { DateService } from "../../../../service/DateService";
import ReportHeader from "../ReportHeader";
import SagoeUi from "../../../assets/fonts/623c7118249e082fe87a78e08506cb4b.ttf";
import SagoeUiItalic from "../../../assets/fonts/a1f05a6515e69ab2886bdaaf1489d410.ttf";
import SagoeUiBold from "../../../assets/fonts/60570a3f64a1fece44ebf71b43617029.ttf";
import { StatusForm } from "../../../../common/Constants";

const Report1Template = ({
  translate,
  data,
  title,
  subtitle,
  loader,
  applicant,
  formVersion,
  formType,
  reportingPeriodId,
  reportingPeriodFormTypeId,
  isPortal
}: {
  translate?: any;
  data?: IFormSectionPresentationModel[];
  title?: string;
  subtitle?: string;
  loader: any;
  applicant?: IReport1Applicant;
  formVersion?: IFormVersionItem;
  formType;
  reportingPeriodId?: number;
  reportingPeriodFormTypeId?: number;
  isPortal: boolean;
}) => {
  const generateCode = () => {
    if (
      reportingPeriodId &&
      applicant &&
      formVersion &&
      reportingPeriodFormTypeId
    ) {
      return `${reportingPeriodId}-${applicant.id}-${reportingPeriodFormTypeId}-${formVersion.version}`;
    }
  };

  const reportCode = generateCode();

  return (
    <Document title={subtitle}>
      <Page size="A4" style={styles.body}>
        <View>
          <ReportHeader
            title={title}
            translate={translate}
            subtitle={subtitle}
            subtitleText={translate("reports.params.reporting_period")}
            reportCode={reportCode}
            otherParams={
              <View>
                {formVersion && (
                  <Text style={styles.subtitle}>
                    {formVersion.status === StatusForm.SEND
                      ? `${translate("report1.form_versions")}: ${formVersion.version} (${formVersion.status}), ${DateService().formaDateWithTime(formVersion.dateSent)} (${translate("report1.date_sent")}), ${formVersion.basisForSending}`
                      : `${translate("report1.form_versions")}: ${formVersion.version}, ${DateService().formaDateWithTime(new Date())} (${translate("report1.date_generated")}), ${formVersion.basisForSending}`}
                  </Text>
                )}
                <Text style={styles.subtitle}>{`${translate(
                  "report1.applicant"
                )}: ${applicant?.fullName}`}</Text>
                <Text style={styles.subtitle}>{`${translate(
                  "report1.form_types"
                )}: ${formType?.name}`}</Text>
              </View>
            }
          />
          <DisplayFormPDF
            formSectionList={data}
            translate={translate}
            isEditableForm={false}
            loading={loader}
            styles={styles}
            isPortal={isPortal}
          />
        </View>
        <Text
          style={styles.pageNumbers}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default Report1Template;

Font.register({
  family: "Segoe UI",
  fonts: [
    {
      src: SagoeUiBold,
      fontWeight: "bold"
    },
    {
      src: SagoeUiItalic,
      fontStyle: "italic"
    },
    { src: SagoeUi }
  ]
});

const styles = StyleSheet.create({
  body: {
    padding: "37pt 37pt 37pt 68pt"
  },
  titleCol: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#CDCDCD",
    padding: "0pt 5pt 5pt",
    marginLeft: "5pt",
    marginBottom: "5pt"
  },
  title: {
    fontSize: "14pt",
    height: "20pt",
    padding: "0pt 5pt 0pt 5pt",
    fontWeight: "bold",
    fontFamily: "Segoe UI"
  },
  subtitle: {
    fontSize: "8pt",
    padding: "0pt 5pt 0pt",
    fontFamily: "Segoe UI"
  },
  header: {
    fontSize: "8pt",
    padding: "5pt 5pt 2pt 5pt",
    fontWeight: "bold",
    fontFamily: "Segoe UI"
  },
  subheader: {
    fontSize: "8pt",
    padding: "0pt 5pt 0pt",
    fontWeight: "bold",
    fontFamily: "Segoe UI"
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    marginLeft: 5,
    paddingBottom: 7
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row"
  },
  tableCol: {
    width: "16.66%",
    borderStyle: "solid",
    borderWidth: 1,
    borderTopWidth: 0
  },
  tableCell: {
    padding: "0pt 5pt 0pt 5pt",
    fontSize: 8,
    fontFamily: "Segoe UI"
  },
  previewTable: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    flexDirection: "column"
  },
  previewRow: {
    margin: "auto",
    flexDirection: "row",
    width: "100%",
    display: "flex"
  },
  qualifier: {
    display: "flex",
    flexDirection: "column",
    paddingTop: 10,
    paddingBottom: 7
  },
  attribute: {
    display: "flex",
    flexDirection: "column",
    width: "100%"
  },
  value: {
    fontSize: "8pt",
    padding: "0pt 5pt 0pt ",
    fontFamily: "Segoe UI"
  },
  comment: {
    fontSize: 8,
    fontFamily: "Segoe UI",
    padding: "0pt 5pt 0pt",
    fontStyle: "italic"
  },
  pageNumbers: {
    fontSize: 10,
    fontFamily: "Segoe UI",
    position: "absolute",
    bottom: "25pt",
    right: "37pt"
  }
});
